/**
 * Packing Management
 */

// React
import React, {FunctionComponent, useEffect, useState, useContext} from 'react';

// Context
import AuthContext from '../../authContext';

// Components
import Pagination from '@material-ui/lab/Pagination';
import List from './components/list';
import Add from './components/add';
import Edit from './components/edit';
import Cancel from './components/cancel';

import RenderFilter from '../../utils/listDaynamicFilterHookForm';

// UI
import {makeStyles, createStyles, Theme} from '@material-ui/core/styles';
import {
  Typography,
  CircularProgress,
  Button,
  Grid,
  FormControl,
  Select,
  MenuItem,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiKeyOutline,
  mdiPlus,
  mdiText,
  mdiAccount,
  mdiRuler,
  mdiWeightKilogram,
} from '@mdi/js';

// Helper
import {removeEmptyValueObject} from '../../utils/helpers';
import useQuery from '../../utils/hook/useQuery';

// Type
import {Props, Status} from './interfaces';
import type {Filter} from '../../utils/listDaynamicFilter';
import ExelExport from './components/exelExport';

import handleError from '../../utils/handleError';
import PackageManagementRepository from 'client-sdk/src/Repository/packagesManagement-repository';

// Style
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    paper: {},
    loading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: '50vh',
      alignSelf: 'center',
      justifyContent: 'center',
      background: '#ccc',
    },
    pagination: {
      // marginTop: theme.spacing(1),
    },
    textArea: {
      width: '100%',
    },
    searchBox: {
      display: 'flex',
      position: 'relative',
      backgroundColor: theme.palette.common.white,
      borderRadius: '4px',
      padding: '10px 0',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
    },

    input: {
      marginLeft: theme.spacing(1),
      flex: 2,
      '@media (max-width: 768px)': {
        fontSize: '12px',
      },
    },
    errorText: {
      paddingTop: '1em',
      fontSize: '0.875rem',
      textAlign: 'left',
    },
    iconButton: {
      padding: 10,
      position: 'relative',
      marginRight: '10px',
      '&:before': {
        content: '""',
        position: 'absolute',
        width: '1px',
        height: '36px',
        top: '4px',
        left: '-9px',
        backgroundColor: '#EDF1F4',
      },
    },
    filterBox: {
      position: 'absolute',
      backgroundColor: '#fff',
      width: '100%',
      top: '47px',
      right: '0',
      marginTop: '30px',
      borderRadius: '0  0 4px  4px',
      padding: '16px 16px',
      boxSizing: 'border-box',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
      zIndex: 10,
    },

    filterOpen: {
      display: 'block',
    },
    filterClose: {
      display: 'none',
    },
    paginationBox: {
      display: 'flex',
    },
    selectSize: {
      width: '35px',
    },
    subtitle: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#8A8A8A',
    },
    magnifyIcon: {
      transform: 'rotate(-90deg)',
    },
    // Cancel Dialog
    cancelIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.error.main,
      },
    },
    cancelTitle: {
      fontSize: '16px',
      fontWeight: 500,
      textAlign: 'center',
      marginBottom: theme.spacing(3),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
    editIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      // marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.blue.main,
      },
    },
    addIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      // marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.green.main,
      },
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
  })
);

const Packing: FunctionComponent<Props> = (props: Props) => {
  const authContext = useContext(AuthContext);
  const {filtersValues, setFiltersValues} = useQuery({
    page: 0,
    size: 10,
  } as any);
  const [filters, setFilters] = useState([] as Filter[]);
  const [dialog, setDialog] = useState(null);
  const [id, setId] = useState(-1 as number);
  const [openSize, setOpenSize] = React.useState(false as boolean);
  const [parcelsList, setParcelsList] = useState([]);
  const [allPackages, setAllPackages] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const filterTranslator = (fieldName: string) => {
    const status = {
      name: 'نام:',
      'name.equals': 'نام:',
      'name.contains': 'نام:',
      code: 'کد:',
      'code.equals': 'کد:',
      'code.contains': 'کد:',
      id: 'شناسه:',
      'id.equals': 'شناسه:',
      'id.contains': 'شناسه:',
      partnerName: 'مالکیت:',
      'partnerName.equals': 'مالکیت:',
      'partnerName.contains': 'مالکیت:',
      length: 'طول:',
      'length.equals': 'طول:',
      'length.contains': 'طول:',
      weight: 'وزن:',
      'weight.equals': 'وزن:',
      'weight.contains': 'وزن:',
    };
    return status[fieldName];
  };
  const removeFilterHandler = (fieldName: any) => {
    const filtered = {...filtersValues};
    delete filtered[fieldName];
    setFiltersValues(filtered);
  };

  useEffect(() => {
    getPackages();
    setupFilters();
  }, [filtersValues]); // eslint-disable-line react-hooks/exhaustive-deps

  const classes = useStyles();
  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setFiltersValues({...filtersValues, page: page - 1});
  };
  const getPackage = async (actions: any) => {
    const payload = {...actions};
    try {
      setLoading(true);
      const {response, count} = await PackageManagementRepository.getPackages({
        payload,
      });
      setParcelsList(response.data);
      setCount(count.data);
    } catch (error) {
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };
  const getPackages = () => {
    const {page, size} = filtersValues;

    getPackage({
      ...removeEmptyValueObject(filtersValues),
      page,
      size,
    });
  };

  const handleChangeSizePage = (event: React.ChangeEvent<{value: unknown}>) => {
    setFiltersValues({...filtersValues, page: 0, size: event.target.value});
  };

  const handleOpen = (type: string, id?: number) => {
    setDialog(type);
    setId(id);
  };

  const handleClose = () => {
    setOpenSize(false);
  };

  const handleOpenConfirmUpdateState = (data: {id: number; body: any}) => {
    setId(data.id);
  };

  const setupFilters = () => {
    setFilters([
      {
        type: 'input',
        id: 'name.contains',
        label: 'نام',
        icon: mdiText,
      },
      {
        type: 'input',
        id: 'id.equals',
        label: 'شناسه',
        icon: mdiKeyOutline,
      },
      {
        type: 'input',
        id: 'code.equals',
        label: 'کد',
        icon: mdiKeyOutline,
      },
      {
        type: 'input',
        id: 'partnerName.contains',
        label: 'مالکیت',
        icon: mdiAccount,
      },
      {
        type: 'input',
        id: 'length.equals',
        label: 'طول',
        icon: mdiRuler,
      },
      {
        type: 'input',
        id: 'width.equals',
        label: 'عرض',
        icon: mdiRuler,
      },
      {
        type: 'input',
        id: 'height.equals',
        label: 'ارتفاع',
        icon: mdiRuler,
      },
      {
        type: 'input',
        id: 'weight.equals',
        label: 'وزن',
        icon: mdiWeightKilogram,
      },
    ]);
  };
  const headCells = [
    {
      id: 'name',
      label: 'نام',
      type: 'input',
    },
    {
      id: 'id',
      label: 'شناسه',
      type: 'input',
    },
    {
      id: 'code',
      label: 'کد',
      type: 'input',
    },
    {
      id: 'partnerName',
      label: 'مالکیت',
      type: 'input',
    },
    {
      id: 'price',
      label: 'قیمت',
      type: 'input',
    },
    {
      id: 'postSizeId',
      label: 'سایز پستی',
      type: 'input',
    },
    {
      id: 'length',
      label: 'ابعاد(سانتی متر)',
      type: 'input',
    },
    {
      id: 'weight',
      label: 'وزن(گرم)',
      type: 'input',
    },
  ];
  const head = {headCells};
  const getAllPackages = async (actions: any) => {
    const payload = {...actions};
    try {
      setLoading(true);
      const response = await PackageManagementRepository.getPackagesExcel({
        payload,
      });
      setAllPackages(response.data);
    } catch (error) {
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };
  const handleOpenConfirmDisable = (id: number) => {
    setId(id);
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={6}>
        <Grid container item xs={12} sm={6} alignItems="center">
          <Typography variant="h4" component="h2">
            {'مدیریت بسته‌بندی'}
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <RenderFilter
            filters={filters}
            onFilter={(filterValues: any) => {
              setFiltersValues({
                ...removeEmptyValueObject(filterValues),
                page: 0,
                size: 10,
              });
            }}
            mainFiltersCount={0}
            baseFilter={{
              type: 'input',
              id: 'id.equals',
              placeholder: 'شناسه',
            }}
            filterValues={filtersValues}
          />
        </Grid>
        <Grid container item xs={12} direction="row-reverse">
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<Icon path={mdiPlus} size={1} />}
            onClick={() => setDialog('ADD_DIALOG')}
          >
            {'بسته‌بندی جدید'}
          </Button>
          <ExelExport
            data={allPackages}
            loading={loading}
            filtersValues={filtersValues}
            handleRequest={getAllPackages}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          {loading ? (
            <div className={classes.loading}>
              <CircularProgress />
            </div>
          ) : (
            <>
              {parcelsList.length > 0 ? (
                <List
                  data={parcelsList}
                  handleOpenConfirmDisable={handleOpenConfirmDisable}
                  handleOpen={handleOpen}
                  changeStatus={handleOpenConfirmUpdateState}
                  head={head}
                  filterVal={filtersValues}
                  onFilterValue={(filtervalues: any) => {
                    setFiltersValues({
                      ...removeEmptyValueObject(filtervalues),
                      ...filtersValues,
                      page: 0,
                      size: 10,
                    });
                  }}
                  removeFilterHandler={removeFilterHandler}
                  filterLabelTranslator={filterTranslator}
                />
              ) : (
                <Typography variant="h3" component="div">
                  محموله موجود نیست
                </Typography>
              )}
            </>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {parcelsList.length > 0 && (
            <FormControl
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <div style={{marginLeft: '10px', color: '#8a8a8a'}}>
                {' '}
                تعداد نمایش در هر صفحه:
              </div>
              <Select
                open={openSize}
                onClose={handleClose}
                onOpen={() => setOpenSize(true)}
                value={filtersValues.size}
                onChange={handleChangeSizePage}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          )}
        </Grid>
        <Grid xs={12} sm={6} container item direction="row-reverse">
          {parcelsList.length > 0 && (
            <Pagination
              className={classes.pagination}
              count={Math.ceil(count / filtersValues.size)}
              color="secondary"
              page={filtersValues.page + 1}
              onChange={handleChangePage}
            />
          )}
        </Grid>
      </Grid>
      <Cancel
        onClose={() => setDialog(null)}
        isOpen={dialog === 'CANCEL_DIALOG' || dialog === 'ACTIVE_DIALOG'}
        id={id}
        updateList={getPackages}
        type={dialog}
      />
      <Add
        onClose={() => setDialog(null)}
        isOpen={dialog === 'ADD_DIALOG'}
        updateList={getPackages}
      />
      <Edit
        onClose={() => setDialog(null)}
        isOpen={dialog === 'EDTI_DIALOG'}
        id={id}
        updateList={getPackages}
      />
    </div>
  );
};
export default Packing;
